import { WarningAmberRounded } from "@mui/icons-material";
import {
    Box,
    Chip,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { KboFlowRunOutput } from "../KboFlowDebug/KboFlowRunDebug";
import { formatDuration, shortId } from "../KboFlowDebug/KboFlowDebugUI";
import {
    DigestCard,
    GroundingChip,
    PhaseStatusChip,
    Stat,
    StatusPill,
    StepNumber,
} from "./KboFlowRunDetailPrimitives";
import { QuestionStepCalls } from "./KboFlowRunLlmCalls";
import { kboFlowRunDetailColors as pageColors } from "./KboFlowRunDetailStyles";
import {
    DigestQuestion,
    asRecord,
    asString,
    contextSectionTitle,
    formatCompactDate,
    formatRunDate,
    getContext,
    getPhase,
    getPursuedQuestionIds,
    questionStatus,
    runOutputStatus,
    shortQuestionLabel,
} from "./KboFlowRunDigestModels";
import {
    ContextSectionItem,
    renderContextSectionDetail,
} from "./KboFlowRunContextAssembly";
import { StepReviewPanel } from "./KboFlowRunFeedback";
import { PhaseTimeline } from "./KboFlowRunPhaseTimeline";

/** Whole-run tab: context assembly, question generation, question validation. */

/** One judge's verdict and the rationale it recorded. */
function JudgeVerdict({
    title,
    verdict,
    reason,
    failed,
}: {
    title: string;
    verdict: string;
    reason?: string;
    failed?: boolean;
}) {
    return (
        <Typography variant="body2" sx={{ mt: 1, "&:first-of-type": { mt: 0 } }}>
            <Box component="span" sx={{ fontWeight: 800 }}>
                {title}{" "}
                <Box
                    component="span"
                    sx={{ color: failed ? "error.main" : undefined }}
                >
                    {verdict}
                </Box>{" "}
                —
            </Box>{" "}
            {reason ?? `No ${title.toLowerCase()} rationale recorded.`}
        </Typography>
    );
}

export function WholeRun({
    output,
    questions,
    onSelectQuestion,
    persistedStatus,
}: {
    output: KboFlowRunOutput;
    questions: DigestQuestion[];
    onSelectQuestion: (questionId?: string) => void;
    persistedStatus?: string | null;
}) {
    const context = getContext(output);
    const identity = context.identity;
    const tenant = context.tenantProfile;
    const tenantContext = asRecord(tenant?.context);
    const user = context.userProfile;
    const userContext = asRecord(user?.context);
    const pursuedIds = getPursuedQuestionIds(output);
    const analyses = output.analysisResults ?? [];
    const reports = output.reports ?? [];
    const phases = output.phaseRecords ?? [];
    const totalFindings = analyses.reduce(
        (total, analysis) =>
            total +
            (analysis.passes ?? []).reduce(
                (passTotal, pass) =>
                    passTotal +
                    (pass.results ?? []).reduce(
                        (resultTotal, result) =>
                            resultTotal + (result.findings?.length ?? 0),
                        0,
                    ),
                0,
            ),
        0,
    );
    const pursuedQuestionId = Array.from(pursuedIds)[0];
    const [selectedValidationId, setSelectedValidationId] = useState<
        string | undefined
    >(pursuedQuestionId ?? questions[0]?.id);
    const userName = asString(user?.name) ?? "the recipient";
    const tenantName = asString(tenant?.name) ?? "this organization";
    const clientType = asString(tenantContext?.clientType);
    const userRole = asString(userContext?.role);
    const parameters = output.request?.parameters;
    const acceptedCount = questions.filter(
        (q) => q.validation?.accepted,
    ).length;
    const [activeCardId, setActiveCardId] = useState("phase-1");
    const runStatus = runOutputStatus(output, persistedStatus);
    const failedExplorationNodeCount = (output.exploration?.nodes ?? []).filter(
        (node) => node.status?.toLowerCase() === "failed",
    ).length;
    const hasCompletedNodeFailures =
        runStatus === "completed" && failedExplorationNodeCount > 0;

    useEffect(() => {
        const cardIds = ["phase-1", "phase-2", "phase-3"];
        const elements = cardIds
            .map((id) => document.getElementById(id))
            .filter((el): el is HTMLElement => !!el);
        if (elements.length === 0) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries.filter((entry) => entry.isIntersecting);
                if (visible.length === 0) return;
                const topMost = visible.reduce((a, b) =>
                    a.boundingClientRect.top <= b.boundingClientRect.top
                        ? a
                        : b,
                );
                setActiveCardId(topMost.target.id);
            },
            { rootMargin: "-96px 0px -70% 0px", threshold: 0 },
        );
        elements.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [output.runId]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Paper
                sx={{
                    background:
                        "linear-gradient(120deg, #315ED6 0%, #3B69DD 100%)",
                    borderRadius: 2,
                    color: pageColors.white,
                    p: { xs: 2.5, md: 3.5 },
                }}
            >
                <Typography
                    variant="overline"
                    sx={{
                        color: pageColors.white,
                        fontWeight: 800,
                        letterSpacing: "0.08em",
                        opacity: 0.8,
                    }}
                >
                    Now viewing
                </Typography>
                <Box
                    sx={{
                        alignItems: "center",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        mt: 0.25,
                    }}
                >
                    <Typography
                        component="h2"
                        sx={{
                            color: pageColors.white,
                            fontSize: { xs: 22, md: 28 },
                            fontWeight: 750,
                        }}
                    >
                        {formatRunDate(output.startedAtUtc)}
                    </Typography>
                    <Chip
                        size="small"
                        label={
                            {
                                completed: "Completed",
                                failed: "Failed",
                                inProgress: "In progress",
                            }[runStatus]
                        }
                        sx={{
                            bgcolor:
                                runStatus === "failed"
                                    ? "rgba(180,35,24,0.85)"
                                    : "rgba(255,255,255,0.18)",
                            color: pageColors.white,
                            fontWeight: 700,
                        }}
                    />
                    {hasCompletedNodeFailures && (
                        <Tooltip title="This run completed despite node-level failures. Its reports use results from completed branches.">
                            <Chip
                                size="small"
                                icon={<WarningAmberRounded />}
                                label={`${failedExplorationNodeCount} node ${
                                    failedExplorationNodeCount === 1
                                        ? "failure"
                                        : "failures"
                                }`}
                                sx={{
                                    bgcolor: "rgba(255,193,7,0.22)",
                                    color: "#FFF4D6",
                                    fontWeight: 700,
                                }}
                            />
                        </Tooltip>
                    )}
                    <Chip
                        size="small"
                        label={formatDuration(
                            output.startedAtUtc,
                            output.completedAtUtc,
                        )}
                        sx={{
                            bgcolor: "rgba(255,255,255,0.18)",
                            color: pageColors.white,
                            fontWeight: 700,
                        }}
                    />
                </Box>
                <Typography
                    sx={{ color: pageColors.white, mt: 0.75, opacity: 0.85 }}
                >
                    {tenantName}
                    {clientType ? ` (${clientType.toLowerCase()})` : ""}
                    {" · requested for "}
                    {userName}
                    {userRole ? `, ${userRole}` : ""}
                    {" · conversation window "}
                    {formatCompactDate(asString(identity?.startDateUtc))}
                    {" – "}
                    {formatCompactDate(asString(identity?.endDateUtc))}
                    {" · run "}
                    {shortId(output.runId)}
                </Typography>
                {parameters && (
                    <Box
                        sx={{
                            bgcolor: "rgba(255,255,255,0.12)",
                            borderRadius: 1.5,
                            display: "inline-block",
                            mt: 1.5,
                            px: 2,
                            py: 1,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                color: pageColors.white,
                                fontWeight: 800,
                                letterSpacing: "0.06em",
                                opacity: 0.75,
                                textTransform: "uppercase",
                            }}
                        >
                            Run parameters
                        </Typography>
                        <Typography
                            sx={{
                                color: pageColors.white,
                                fontWeight: 700,
                                mt: 0.25,
                            }}
                        >
                            target {parameters.targetQuestionCount ?? "—"}{" "}
                            questions · pursue{" "}
                            {parameters.maxQuestionsToPursue ?? "—"} · history{" "}
                            {parameters.ignoreHistory
                                ? "ignored"
                                : "considered"}
                        </Typography>
                    </Box>
                )}
            </Paper>

            <Box
                sx={{
                    display: "grid",
                    gap: 1.25,
                    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                }}
            >
                <Stat
                    value={context.sectionStatuses?.length ?? 0}
                    label="context sections assembled"
                />
                <Stat value={questions.length} label="questions generated" />
                <Stat
                    value={`${acceptedCount}/${questions.length}`}
                    label="passed all four judges"
                />
                <Stat value={pursuedIds.size} label="question pursued" />
                <Stat value={totalFindings} label="candidate findings" />
                <Stat value={reports.length} label="reports generated" />
            </Box>

            <Box
                sx={{
                    alignItems: "start",
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                        xs: "1fr",
                        lg: "260px minmax(0, 1fr)",
                    },
                }}
            >
                <PhaseTimeline
                    output={output}
                    phases={phases}
                    activeCardId={activeCardId}
                />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <DigestCard
                        id="phase-1"
                        number={1}
                        title="Context assembly"
                        meta={
                            <Box sx={{ display: "flex", gap: 0.75 }}>
                                <PhaseStatusChip
                                    phase={getPhase(output, "ContextAssembly")}
                                />
                                <Chip
                                    size="small"
                                    label={formatDuration(
                                        getPhase(output, "ContextAssembly")
                                            ?.startedAtUtc,
                                        getPhase(output, "ContextAssembly")
                                            ?.completedAtUtc,
                                    )}
                                />
                            </Box>
                        }
                    >
                        <Typography
                            sx={{
                                color: pageColors.muted,
                                lineHeight: 1.6,
                                mb: 2,
                            }}
                        >
                            Before writing anything, KBo pulls{" "}
                            {context.sectionStatuses?.length ?? "several"}{" "}
                            sections of tenant reality into one snapshot. No
                            model runs here — it is straight retrieval, and it
                            is the only thing the question writer is allowed to
                            reason from.
                        </Typography>
                        <Box>
                            {(context.sectionStatuses ?? []).map(
                                (status, index) => {
                                    const key =
                                        status.section
                                            ?.toLowerCase()
                                            .replace(/[^a-z]/g, "") ?? "";
                                    const isHistory =
                                        key === "generationhistory";
                                    const historyIgnored =
                                        isHistory &&
                                        output.request?.parameters
                                            ?.ignoreHistory;
                                    return (
                                        <ContextSectionItem
                                            key={status.section ?? index}
                                            title={contextSectionTitle(
                                                status.section,
                                            )}
                                            note={
                                                historyIgnored
                                                    ? "loaded, then ignored — the run was set to skip prior history"
                                                    : undefined
                                            }
                                        >
                                            {renderContextSectionDetail(
                                                key,
                                                context,
                                            )}
                                        </ContextSectionItem>
                                    );
                                },
                            )}
                        </Box>
                    </DigestCard>

                    <DigestCard
                        id="phase-2"
                        number={2}
                        title="Question generation"
                        meta={
                            <Typography variant="body2" color="text.secondary">
                                {questions.length} questions from the strongest
                                signals
                            </Typography>
                        }
                    >
                        <Typography
                            sx={{
                                color: pageColors.muted,
                                lineHeight: 1.6,
                                mb: 2,
                            }}
                        >
                            One call writes {questions.length} candidate
                            questions. Each must cite the signals it came from —
                            a topic, a classifier, a journey stage, or a program
                            event — so the grounding is auditable rather than
                            asserted.
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                            }}
                        >
                            {questions.map((question, index) => {
                                const pursued = pursuedIds.has(
                                    question.id ?? "",
                                );
                                return (
                                    <Paper
                                        key={question.id ?? index}
                                        variant="outlined"
                                        sx={{
                                            borderColor: pursued
                                                ? pageColors.blue
                                                : "divider",
                                            p: 1.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                alignItems: "flex-start",
                                                display: "flex",
                                                gap: 1.25,
                                            }}
                                        >
                                            <StepNumber
                                                number={index + 1}
                                                color={pageColors.blue}
                                            />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 700 }}
                                                >
                                                    {question.text}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mt: 0.35 }}
                                                >
                                                    {question.rationale}
                                                </Typography>
                                            </Box>
                                            <StatusPill
                                                status={questionStatus(
                                                    output,
                                                    question,
                                                )}
                                            />
                                        </Box>
                                        {(question.groundingSignals ?? [])
                                            .length > 0 && (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: 0.75,
                                                    mt: 1,
                                                    pl: { xs: 0, sm: 5 },
                                                }}
                                            >
                                                {question.groundingSignals?.map(
                                                    (signal, signalIndex) => (
                                                        <GroundingChip
                                                            key={`${signal.kind}-${signalIndex}`}
                                                            signal={signal}
                                                            emphasis={pursued}
                                                        />
                                                    ),
                                                )}
                                            </Box>
                                        )}
                                        {question.id && (
                                            <StepReviewPanel
                                                questionId={question.id}
                                                scope={{ kind: "question" }}
                                                label="Review this question"
                                                placeholder="Was this the right question to ask this user, and is its grounding sound?"
                                            />
                                        )}
                                    </Paper>
                                );
                            })}
                        </Box>
                        {/* One call writes every candidate question, so the
                            prompt belongs to the step rather than to each row. */}
                        <QuestionStepCalls
                            output={output}
                            phase="QuestionGeneration"
                        />
                    </DigestCard>

                    <DigestCard
                        id="phase-3"
                        number={3}
                        title="Question validation"
                        meta={
                            <Typography variant="body2" color="text.secondary">
                                four judges per question
                            </Typography>
                        }
                    >
                        <Typography
                            sx={{
                                color: pageColors.muted,
                                lineHeight: 1.6,
                                mb: 2,
                            }}
                        >
                            Each question faces four independent judges in
                            parallel. Specificity and feasibility are pass/fail
                            gates; alignment and actionability are scored 0–3
                            and set the ranking.{" "}
                            {acceptedCount === questions.length
                                ? `All ${questions.length} passed`
                                : `${acceptedCount} of ${questions.length} passed`}{" "}
                            — only the top-ranked{" "}
                            {(output.request?.parameters
                                ?.maxQuestionsToPursue ?? 1) === 1
                                ? "one is"
                                : `${output.request?.parameters?.maxQuestionsToPursue} are`}{" "}
                            pursued.
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gap: 1,
                                gridTemplateColumns:
                                    "repeat(auto-fit, minmax(200px, 1fr))",
                                mb: 2,
                            }}
                        >
                            {[
                                {
                                    title: "Specificity",
                                    caption: "gate",
                                    description:
                                        "Is there a concrete scope and target to research?",
                                },
                                {
                                    title: "Feasibility",
                                    caption: "gate",
                                    description:
                                        "Can this tenant's existing models answer it?",
                                },
                                {
                                    title: "Alignment",
                                    caption: "0–3",
                                    description:
                                        "How much should it matter to this customer?",
                                },
                                {
                                    title: "Actionability",
                                    caption: "0–3",
                                    description:
                                        "Does it point at a lever they control?",
                                },
                            ].map((judge) => (
                                <Paper
                                    key={judge.title}
                                    variant="outlined"
                                    sx={{ p: 1.5 }}
                                >
                                    <Typography sx={{ fontWeight: 750 }}>
                                        {judge.title}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {judge.caption}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: pageColors.muted,
                                            mt: 0.5,
                                        }}
                                    >
                                        {judge.description}
                                    </Typography>
                                </Paper>
                            ))}
                        </Box>
                        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "#F8FAFF" }}>
                                        <TableCell
                                            sx={{
                                                color: pageColors.muted,
                                                fontWeight: 800,
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            Question
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: pageColors.muted,
                                                fontWeight: 800,
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            Specific
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: pageColors.muted,
                                                fontWeight: 800,
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            Feasible
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: pageColors.muted,
                                                fontWeight: 800,
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            Align
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: pageColors.muted,
                                                fontWeight: 800,
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            Actionability
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {questions.map((question, index) => {
                                        const selected =
                                            !!question.id &&
                                            question.id ===
                                                selectedValidationId;
                                        return (
                                            <TableRow
                                                key={question.id ?? index}
                                                onClick={() =>
                                                    setSelectedValidationId(
                                                        question.id,
                                                    )
                                                }
                                                sx={{
                                                    bgcolor: selected
                                                        ? pageColors.blueSoft
                                                        : undefined,
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <TableCell
                                                    sx={{
                                                        color: selected
                                                            ? pageColors.blue
                                                            : pageColors.ink,
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    Q{index + 1} ·{" "}
                                                    {shortQuestionLabel(
                                                        question,
                                                    )}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        color: question
                                                            .validation
                                                            ?.specificity
                                                            ?.passed
                                                            ? pageColors.green
                                                            : "#B42318",
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {question.validation
                                                        ?.specificity?.passed
                                                        ? "pass"
                                                        : "fail"}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        color: question
                                                            .validation
                                                            ?.feasibility
                                                            ?.passed
                                                            ? pageColors.green
                                                            : "#B42318",
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {question.validation
                                                        ?.feasibility?.passed
                                                        ? "pass"
                                                        : "fail"}
                                                </TableCell>
                                                <TableCell
                                                    sx={{ fontWeight: 700 }}
                                                >
                                                    {question.validation
                                                        ?.alignment?.score ??
                                                        "—"}
                                                    /3
                                                </TableCell>
                                                <TableCell
                                                    sx={{ fontWeight: 700 }}
                                                >
                                                    {question.validation
                                                        ?.actionability
                                                        ?.score ?? "—"}
                                                    /3
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Paper>
                        {questions
                            .map((question, index) => ({ question, index }))
                            .filter(
                                ({ question }) =>
                                    !!question.id &&
                                    question.id === selectedValidationId,
                            )
                            .map(({ question, index }) => (
                                <Paper
                                    key={question.id ?? index}
                                    variant="outlined"
                                    sx={{
                                        bgcolor: pageColors.blueSoft,
                                        borderColor: pageColors.blue,
                                        mt: 2,
                                        p: 2,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: pageColors.blue,
                                            fontWeight: 800,
                                        }}
                                    >
                                        What the judges extracted from Q
                                        {index + 1} — this is what the analysis
                                        is held to
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gap: 1.25,
                                            gridTemplateColumns: {
                                                xs: "1fr",
                                                md: "repeat(3, 1fr)",
                                            },
                                            mt: 1.5,
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: pageColors.muted,
                                                    fontWeight: 800,
                                                    textTransform: "uppercase",
                                                }}
                                            >
                                                Scope
                                            </Typography>
                                            <Typography sx={{ mt: 0.25 }}>
                                                {question.validation?.scope ??
                                                    "—"}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: pageColors.muted,
                                                    fontWeight: 800,
                                                    textTransform: "uppercase",
                                                }}
                                            >
                                                Target
                                            </Typography>
                                            <Typography sx={{ mt: 0.25 }}>
                                                {question.validation?.target ??
                                                    "—"}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: pageColors.muted,
                                                    fontWeight: 800,
                                                    textTransform: "uppercase",
                                                }}
                                            >
                                                Lever
                                            </Typography>
                                            <Typography sx={{ mt: 0.25 }}>
                                                {question.validation?.lever ??
                                                    "—"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider
                                        sx={{
                                            borderStyle: "dashed",
                                            my: 1.5,
                                        }}
                                    />
                                    {/* Gates first, then the scored dimensions —
                                        the same order as the table above. */}
                                    <JudgeVerdict
                                        title="Specificity"
                                        verdict={
                                            question.validation?.specificity
                                                ?.passed
                                                ? "pass"
                                                : "fail"
                                        }
                                        failed={
                                            question.validation?.specificity
                                                ?.passed === false
                                        }
                                        reason={
                                            question.validation?.specificity
                                                ?.reason
                                        }
                                    />
                                    <JudgeVerdict
                                        title="Feasibility"
                                        verdict={
                                            question.validation?.feasibility
                                                ?.passed
                                                ? "pass"
                                                : "fail"
                                        }
                                        failed={
                                            question.validation?.feasibility
                                                ?.passed === false
                                        }
                                        reason={
                                            question.validation?.feasibility
                                                ?.reason
                                        }
                                    />
                                    <JudgeVerdict
                                        title="Alignment"
                                        verdict={`${
                                            question.validation?.alignment
                                                ?.score ?? "—"
                                        }/3`}
                                        reason={
                                            question.validation?.alignment
                                                ?.reason
                                        }
                                    />
                                    <JudgeVerdict
                                        title="Actionability"
                                        verdict={`${
                                            question.validation?.actionability
                                                ?.score ?? "—"
                                        }/3`}
                                        reason={
                                            question.validation?.actionability
                                                ?.reason
                                        }
                                    />
                                    <QuestionStepCalls
                                        output={output}
                                        questionId={question.id}
                                        phase="QuestionValidation"
                                    />
                                    {question.id && (
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                bgcolor: pageColors.white,
                                                borderRadius: 1.5,
                                                mt: 2,
                                                p: 2,
                                            }}
                                        >
                                            <StepReviewPanel
                                                standalone
                                                questionId={question.id}
                                                scope={{
                                                    kind: "step",
                                                    step: "QuestionValidation",
                                                }}
                                                label="Review how it was judged"
                                                placeholder="Did the judges score this question correctly — and for the right reasons?"
                                            />
                                        </Paper>
                                    )}
                                </Paper>
                            ))}
                    </DigestCard>

                    {pursuedQuestionId && (
                        <Paper
                            variant="outlined"
                            sx={{
                                bgcolor: pageColors.blueSoft,
                                borderColor: pageColors.blue,
                                borderRadius: 2,
                                p: 2,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ color: pageColors.ink }}
                            >
                                The pursued question's multi-level analysis and
                                delivered report live on its own tab —{" "}
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() =>
                                        onSelectQuestion(pursuedQuestionId)
                                    }
                                    sx={{
                                        bgcolor: "transparent",
                                        border: 0,
                                        color: pageColors.blue,
                                        cursor: "pointer",
                                        font: "inherit",
                                        fontWeight: 700,
                                        p: 0,
                                        textDecoration: "underline",
                                    }}
                                >
                                    view the analysis and report
                                </Box>
                                .
                            </Typography>
                        </Paper>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
